'use client';

import { ProductProps } from '@/types/Product';
import { Box, Flex, GridItem, Icon, Text } from '@chakra-ui/react';
import Image from 'next/image';
import Link from 'next/link';
import { FaArrowRight, FaCircleInfo } from 'react-icons/fa6';
import { useEffect, useState } from 'react';
import { usePromotions } from '@/contexts/PromotionsContext';
import { getMinimumValueHint, getProductDiscount } from '@/utils/getProductDiscount';
import { Tooltip } from '@/components/ui/tooltip';
import WishlistButton from '@/components/WishlistButton';

type ProductCardProps = {
  product: ProductProps;
  /** Cartão reduzido, usado na vista em grelha (2 colunas) do mobile. */
  compact?: boolean;
};

const StockDot = ({ inStock }: { inStock: boolean }) => (
  <Flex
    align="center"
    gap="4px"
    px="8px"
    py="3px"
    borderRadius="full"
    bg={inStock ? 'rgba(0,200,110,0.14)' : 'rgba(220,40,40,0.14)'}
    backdropFilter="blur(8px)"
  >
    <Box
      w="6px"
      h="6px"
      borderRadius="full"
      bg={inStock ? '#00C86E' : '#DC2828'}
      flexShrink={0}
    />
    <Text
      fontSize="10px"
      fontWeight="700"
      color={inStock ? '#00C86E' : '#DC2828'}
      lineHeight="1"
    >
      {inStock ? 'Em stock' : 'Sem stock'}
    </Text>
  </Flex>
);

const PreSaleDot = () => (
  <Flex
    align="center"
    gap="4px"
    px="8px"
    py="3px"
    borderRadius="full"
    bg="rgba(124,58,237,0.14)"
    backdropFilter="blur(8px)"
  >
    <Box w="6px" h="6px" borderRadius="full" bg="#7C3DED" flexShrink={0} />
    <Text fontSize="10px" fontWeight="700" color="#7C3DED" lineHeight="1">
      Pré-venda
    </Text>
  </Flex>
);

const ComingSoonDot = () => (
  <Flex
    align="center"
    gap="4px"
    px="8px"
    py="3px"
    borderRadius="full"
    bg="rgba(0,176,202,0.14)"
    backdropFilter="blur(8px)"
  >
    <Box w="6px" h="6px" borderRadius="full" bg="#00B0CA" flexShrink={0} />
    <Text fontSize="10px" fontWeight="700" color="#00B0CA" lineHeight="1">
      Brevemente
    </Text>
  </Flex>
);

const ProductCard = ({ product, compact = false }: ProductCardProps) => {
  const [image, setImage] = useState('/ido-logo.png');

  useEffect(() => {
    if (product.images.length > 0) {
      const defaultImg = product.images.find((i) => i.default);
      if (defaultImg && defaultImg.path) setImage(defaultImg.path);
    }
  }, [product]);

  const getStockStatus = (): boolean | null => {
    if (product.product_type === 'SIMPLE' && product.simple)
      return product.simple.stock > 0;
    if (product.product_type === 'VARIATION' && product.variations)
      return product.variations.some((v) => v.stock > 0);
    if (product.product_type === 'COLOR_SIZE' && product.colors)
      return product.colors.some((c) =>
        c.sizes ? c.sizes.some((s) => s.stock > 0) : false,
      );
    if (product.product_type === 'CONTACT_LENSES' && product.contact_lenses)
      return product.contact_lenses.stock > 0;
    return null;
  };

  const getMinPrice = () => {
    if (product.product_type === 'SIMPLE' && product.simple)
      return { price: product.simple.price_tax, prefix: '' };
    if (product.product_type === 'VARIATION' && product.variations) {
      const prices = product.variations.map((v) => v.price_tax).flat();
      return { price: Math.min(...prices), prefix: 'Desde ' };
    }
    if (product.product_type === 'COLOR_SIZE' && product.colors) {
      const prices = product.colors
        .map((c) =>
          c.sizes.length > 0 ? c.sizes.map((s) => s.price_tax) : [],
        )
        .flat();
      return { price: Math.min(...prices), prefix: 'Desde ' };
    }
    if (product.product_type === 'CONTACT_LENSES' && product.contact_lenses)
      return { price: product.contact_lenses.price_tax, prefix: '' };
    return null;
  };

  const { promotions } = usePromotions();
  // Produtos "brevemente" não têm preço público — nem campanhas aplicáveis.
  const discount = product.coming_soon
    ? null
    : getProductDiscount(product, promotions);
  const minValueHint = discount ? getMinimumValueHint(discount.rule) : null;

  const stockStatus = getStockStatus();
  const priceInfo = getMinPrice();

  const formatPrice = (value: number) =>
    new Intl.NumberFormat('pt-PT', {
      style: 'currency',
      currency: 'EUR',
      currencyDisplay: 'symbol',
      currencySign: 'accounting',
    }).format(value);

  return (
    <Link href={`/produto/${product.slug}`} key={product.id}>
      <GridItem
        w={compact ? 'full' : '260px'}
        minH={compact ? '260px' : '380px'}
        borderRadius="20px"
        bg={{ base: '#FFFFFF', _dark: '#0A1B2B' }}
        display="flex"
        alignItems="center"
        flexDir="column"
        transition="all 0.32s cubic-bezier(0.4,0,0.2,1)"
        _hover={{
          transform: 'translateY(-4px)',
          boxShadow: '0 20px 60px rgba(0,176,202,0.18), 0 4px 16px rgba(0,0,0,0.08)',
          borderColor: 'rgba(0,176,202,0.45)',
        }}
        border="2px solid"
        borderColor={{ base: 'rgba(0,0,0,0.07)', _dark: 'rgba(255,255,255,0.06)' }}
        pos="relative"
        overflow="hidden"
        boxShadow="0 1px 6px rgba(0,0,0,0.05)"
        cursor="pointer"
        role="group"
      >
        {/* Hover overlay CTA */}
        {!compact && (
          <Flex
            pos="absolute"
            bottom="0"
            left="0"
            right="0"
            h="48px"
            bg="#00B0CA"
            align="center"
            justify="center"
            gap="8px"
            opacity="0"
            transform="translateY(100%)"
            transition="all 0.28s cubic-bezier(0.4,0,0.2,1)"
            zIndex="3"
            _groupHover={{ opacity: '1', transform: 'translateY(0)' }}
          >
            <Text fontSize="13px" fontWeight="700" color="white" letterSpacing="0.04em">
              VER PRODUTO
            </Text>
            <Icon w="13px" h="13px" color="white">
              <FaArrowRight />
            </Icon>
          </Flex>
        )}

        <WishlistButton
          productId={product.id}
          productOptId={null}
          productOptLabel={null}
          pos="absolute"
          bottom="10px"
          right="12px"
          zIndex="5"
        />

        {/* Image area */}
        <Flex
          w="100%"
          h={compact ? '140px' : '220px'}
          bg={{ base: '#F8FAFB', _dark: '#0E2030' }}
          borderTopRadius="19px"
          pos="relative"
          overflow="hidden"
          align="center"
          justify="center"
          flexShrink={0}
        >
          {/* Brand badge top-left */}
          {product.brand && (
            <Box pos="absolute" top="10px" left="10px" zIndex="2">
              <Text
                fontSize="9px"
                fontWeight="800"
                px="7px"
                py="2px"
                borderRadius="4px"
                bg="rgba(10,27,43,0.72)"
                color="rgba(255,255,255,0.92)"
                letterSpacing="0.08em"
                textTransform="uppercase"
                backdropFilter="blur(8px)"
              >
                {product.brand.name}
              </Text>
            </Box>
          )}

          {/* Discount badge top-right */}
          {discount ? (
            <Flex
              pos="absolute"
              top="10px"
              right="10px"
              zIndex="2"
              align="center"
              gap="5px"
              pl="9px"
              pr={minValueHint ? '5px' : '9px'}
              py="4px"
              borderRadius="full"
              bg={discount.color}
              backdropFilter="blur(8px)"
              boxShadow="0 3px 10px rgba(0,0,0,0.22), inset 0 1px 0 rgba(255,255,255,0.25)"
              title={discount.label}
            >
              <Text fontSize="10px" fontWeight="800" color="white" letterSpacing="0.04em">
                {discount.badge}
              </Text>
              {minValueHint && (
                <Tooltip content={minValueHint} showArrow>
                  <Flex
                    as="span"
                    w="15px"
                    h="15px"
                    borderRadius="full"
                    bg="rgba(255,255,255,0.25)"
                    align="center"
                    justify="center"
                    cursor="help"
                    transition="all 0.18s ease"
                    _hover={{ bg: 'rgba(255,255,255,0.4)', transform: 'scale(1.15)' }}
                  >
                    <Icon w="8px" h="8px" color="white">
                      <FaCircleInfo />
                    </Icon>
                  </Flex>
                </Tooltip>
              )}
            </Flex>
          ) : product.coming_soon ? (
            <Box pos="absolute" top="10px" right="10px" zIndex="2">
              <ComingSoonDot />
            </Box>
          ) : product.is_pre_sale ? (
            <Box pos="absolute" top="10px" right="10px" zIndex="2">
              <PreSaleDot />
            </Box>
          ) : (
            product.show_stock &&
            stockStatus !== null && (
              <Box pos="absolute" top="10px" right="10px" zIndex="2">
                <StockDot inStock={stockStatus} />
              </Box>
            )
          )}

          <Box
            transition="transform 0.36s ease"
            _groupHover={{ transform: 'scale(1.05)' }}
          >
            <Image
              src={image || '/logo-idu.svg'}
              alt={product.name}
              width={compact ? 130 : 220}
              height={compact ? 110 : 180}
              style={{
                objectFit: 'contain',
                maxHeight: compact ? '110px' : '180px',
                maxWidth: compact ? '130px' : '220px',
                width: 'auto',
                height: 'auto',
              }}
            />
          </Box>

          {/* Color swatches */}
          {product.product_type === 'COLOR_SIZE' && product.colors && (
            <Flex
              pos="absolute"
              bottom="8px"
              right="8px"
              gap="3px"
              align="center"
              zIndex="1"
            >
              {product.colors.slice(0, 5).map((color) => (
                <Box
                  key={color.id}
                  w="12px"
                  h="12px"
                  borderRadius="full"
                  border="1.5px solid rgba(255,255,255,0.8)"
                  bg={
                    color.color_codes.includes(',')
                      ? `linear-gradient(to right, ${color.color_codes.split(',').map((c) => c.trim()).join(', ')})`
                      : color.color_codes || '#ccc'
                  }
                  boxShadow="0 1px 3px rgba(0,0,0,0.2)"
                />
              ))}
              {product.colors.length > 5 && (
                <Text fontSize="9px" fontWeight="700" color="white" textShadow="0 1px 3px rgba(0,0,0,0.5)">
                  +{product.colors.length - 5}
                </Text>
              )}
            </Flex>
          )}
        </Flex>

        {/* Thin separator */}
        <Box w="100%" h="1px" bg={{ base: 'rgba(0,0,0,0.05)', _dark: 'rgba(255,255,255,0.05)' }} />

        {/* Content */}
        <Flex
          flexDir="column"
          w="full"
          flex="1"
          px={compact ? '10px' : '16px'}
          pt={compact ? '10px' : '14px'}
          pb={compact ? '12px' : '56px'}
        >
          {/* Category */}
          {product.categorie_main && !compact && (
            <Text
              fontSize="10px"
              fontWeight="700"
              color={{ base: 'rgba(0,176,202,0.8)', _dark: 'rgba(0,176,202,0.7)' }}
              letterSpacing="0.1em"
              textTransform="uppercase"
              mb="5px"
            >
              {product.categorie_main.name}
            </Text>
          )}

          {/* Product name */}
          <Text
            fontSize={compact ? '12px' : '14px'}
            fontWeight="700"
            lineHeight="1.3"
            color={{ base: '#0A1B2B', _dark: '#E2EFF5' }}
            mb="6px"
            style={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {product.name}
          </Text>

          {/* Price */}
          {product.coming_soon && (
            <Flex flexDir="column" gap="2px" mt="auto">
              <Text
                fontSize={compact ? '14px' : '16px'}
                fontWeight="800"
                color="#00B0CA"
                letterSpacing="-0.3px"
                lineHeight="1.1"
              >
                Disponível brevemente
              </Text>
              {product.coming_soon_note && (
                <Text
                  fontSize="11px"
                  color={{ base: '#7A9AAE', _dark: '#5A8099' }}
                  fontWeight="500"
                >
                  {product.coming_soon_note}
                </Text>
              )}
            </Flex>
          )}
          {!product.coming_soon && priceInfo && (
            <Flex flexDir="column" gap="2px" mt="auto">
              {discount?.hasPriceReduction && (
                <Flex align="baseline" gap="4px">
                  {priceInfo.prefix && (
                    <Text fontSize="9px" color={{ base: '#7A9AAE', _dark: '#5A8099' }} fontWeight="500">
                      {priceInfo.prefix}
                    </Text>
                  )}
                  <Text
                    fontSize={compact ? '11px' : '13px'}
                    fontWeight="500"
                    color={{ base: '#9AACBA', _dark: '#4A6070' }}
                    letterSpacing="-0.3px"
                    lineHeight="1"
                    textDecoration="line-through"
                  >
                    {formatPrice(priceInfo.price)}
                  </Text>
                </Flex>
              )}
              <Flex align="baseline" gap="4px">
                {priceInfo.prefix && !discount?.hasPriceReduction && (
                  <Text fontSize="10px" color={{ base: '#7A9AAE', _dark: '#5A8099' }} fontWeight="500">
                    {priceInfo.prefix}
                  </Text>
                )}
                <Text
                  fontSize={compact ? '16px' : '20px'}
                  fontWeight="800"
                  color={discount?.hasPriceReduction ? discount.color : { base: '#0A1B2B', _dark: '#E2EFF5' }}
                  letterSpacing="-0.5px"
                  lineHeight="1"
                >
                  {formatPrice(discount ? discount.discountedPrice : priceInfo.price)}
                </Text>
              </Flex>
            </Flex>
          )}
        </Flex>
      </GridItem>
    </Link>
  );
};

export default ProductCard;
